import {
    Box,
    Palette,
    PaletteColor,
    Typography
} from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import PeriodicChips from "../../components/periodics/PeriodicChips";
import DataTable, { IColumn } from "../../components/table/data/DataTable";
import { IFilter } from "../../components/table/data/filter/DataFilter";
import Toolbar from "../../components/toolbar/Toolbar";
import InfoToolTip from "../../components/toolTip/InfoToolTip";
import {
    EPeriodicStatus,
    PeriodicDisplay,
    PeriodicDisplayDate,
} from "../../models/periodic/periodic";
import auth from "../../services/auth/AuthService";
import periodidService from "../../services/periodic/PeriodicService";
import DownloadTable from "../../utils/download/downloadDataTable";
import { useGetPeriodics } from "../../hooks/periodic/useGetPeriodics";
import { useTranslatedTaskName } from "../../utils/taskNameTranslation";

interface StatusInfo {
    key: string;
    color: keyof Palette | "default";
}

export type Color =
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning";

const usePeriodicColumns = () => {
    const { t } = useTranslation(["periodical"]);
    const translateTaskName = useTranslatedTaskName();

    const statusInfo: StatusInfo[] = [
        {
            key: EPeriodicStatus[EPeriodicStatus.Todo],
            color: "default",
        },
        {
            key: EPeriodicStatus[EPeriodicStatus.ToVerify],
            color: "info",
        },
        {
            key: EPeriodicStatus[EPeriodicStatus.Accepted],
            color: "success",
        },
        {
            key: EPeriodicStatus[EPeriodicStatus.Refused],
            color: "error",
        },
        { key: "Overdue", color: "warning" },
    ];
    const tooltipContent = statusInfo.map((status) => (
        <>
            <Typography
                variant="subtitle2"
                display="inline"
                sx={{
                    color: (theme) =>
                        status.color === "default"
                            ? theme.palette.grey[500]
                            : (theme.palette[status.color] as PaletteColor)
                                .main,
                }}
            >
                {t(`periodical:status.${status.key}`)}
            </Typography>
            {t(`periodical:statusDescription.${status.key}`)}
            <br />
        </>
    ));

    const columns: IColumn<PeriodicDisplay>[] = [
        { field: "building", headerName: t("common:header.building") },
        {
            field: "room",
            headerName: t("common:header.room"),
            transform: {
                render: (data) => <Typography variant="body2">{data.room.name}</Typography>,
                filterTransform: (data) => {
                    return data.room.name;
                },
            },
        },
        { field: "floor", headerName: t("common:header.floor") },
        { field: "profile", headerName: t("common:local.profile") },
        {
            field: "task",
            headerName: t("common:header.task"),
            transform: {
                render: (data) => <Typography variant="body2">{translateTaskName(data.task)}</Typography>,
                filterTransform: (data) => {
                    return translateTaskName(data.task);
                },
            },
        },
        {
            field: "cleaningDate",
            headerName: t("common:header.cleaningDates"),
            renderHeader: (
                <InfoToolTip>
                    <>
                        {tooltipContent}
                    </>
                </InfoToolTip>
            ),
            transform: {
                render: (data) => (
                    <PeriodicChips periodic={data.cleaningDate} />
                ),
                filterTransform: (data) => {
                    return moment
                        .utc(data.cleaningDate.scheduleDate)
                        .format("DD-MM-YYYY") +
                        " (" + t(`periodical:status.${EPeriodicStatus[data.cleaningDate.status]}`) + ")";
                },
            },
        },
    ];

    return columns;
};

const PeriodicPage = () => {
    const { t } = useTranslation(["periodical"]);

    const [isFilterActive, setIsFilterActive] = useState<boolean>(true);
    const navigate = useNavigate();
    const columns = usePeriodicColumns();
    const [filteredRows, setFilteredRows] = useState<PeriodicDisplay[]>([]);
    const { data: periodics = [], isLoading } = useGetPeriodics(auth.user?.companyId || 0);
    const [selectedFilters, setSelectedFilters] = useState<Map<string, IFilter>>(new Map());

    useEffect(() => {
        if (periodics.length > 0) {
            setFilteredRows(periodics);
        }
    }, [periodics]);


    const visibleFilters: string[] = [];
    selectedFilters.forEach((filter, field) => {
        if (!filter.isActive || !filter.request) return;
        const column = columns.find((column) => column.field === field);
        visibleFilters.push(`${column?.headerName}: ${t(`filter.${filter.type}`)} ${filter.request}`);
    });

    const downloadTable = new DownloadTable("periodic", filteredRows, columns, t("common:table.periodic.title"), visibleFilters);


    return (
        <Box sx={{ width: "100%", height: "100%" }}>
            <Toolbar
                onFilterClick={() => setIsFilterActive((old) => !old)}
                onCreate={() => navigate("add")}
                isFilterActive={isFilterActive}
                downloadButtons={[
                    { label: "Excel", onClick: () => downloadTable.GetExcel(t("common:header.exportedBy"), t("common:header.exportDate")) },
                    { label: "CSV", onClick: () => downloadTable.GetCSV() },
                    { label: "PDF", onClick: () => downloadTable.GetPeriodicPDF(t("common:header.exportedBy"), t("common:header.exportDate")) }
                ]}
            />

            <DataTable
                dense
                disableFiltrer={!isFilterActive}
                rows={periodics}
                isLoading={isLoading}
                columns={columns}
                onRowClick={(row) => {
                    navigate("PeriodicValidation", { state: { ...row } });
                }}
                getRowNavTo={(row) => "PeriodicValidation"}
                onSetFilter={(filteredRows, selectedFilters) => {
                    setFilteredRows(filteredRows)
                    setSelectedFilters(selectedFilters)
                }}
            />
        </Box>
    );
};

export default PeriodicPage;
